import React, { useState } from "react";
import { IonIcon, IonModal } from "@ionic/react";
import { sendSharp } from "ionicons/icons";
import IonDateRangeModal from "../../../components/datePicker/datePicker"; // Import the standalone modal
import { AdherenceData } from "../../../contexts/types";
import { AdherenceLoading } from "./ProgressIndicator";
import { useBLE } from "../../../hooks/useBle";
import { Capacitor } from "@capacitor/core";
import { ScreenOrientation } from "@capacitor/screen-orientation";

// Main Adherence Summary Card Component
const AdherenceSummaryCard: React.FC<{
  data: AdherenceData | null;
  isLoading: boolean;
  isClearDevice?: boolean;
  onSync: (dateRange: { start: string; end: string; tz: string }) => void;
}> = ({ data, isLoading, isClearDevice, onSync }) => {
  type TabType = "week" | "month" | "range";
  const [activeTab, setActiveTab] = useState<TabType>("week");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [customDateRange, setCustomDateRange] = useState<{
    start: string;
    end: string;
  } | null>(null);

  // **NEW: Landscape mode state**
  const [forcedLandscape, setForcedLandscape] = useState(false);
  const [preLandscapeState, setPreLandscapeState] = useState<{
    activeTab: TabType;
    customDateRange: { start: string; end: string } | null;
  } | null>(null);

  const isLandscape = forcedLandscape || window.innerWidth > window.innerHeight;

  const { isConnected, storedAdherenceDeviceId } = useBLE();

  // **NEW: Landscape orientation functions**
  const forceEnterLandscape = async () => {
    setPreLandscapeState({
      activeTab,
      customDateRange,
    });
    setForcedLandscape(true);
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "landscape" });
      } catch (error) {
        console.warn("Failed to force landscape:", error);
      }
    }
  };

  const exitLandscape = async () => {
    setForcedLandscape(false);
    if (preLandscapeState) {
      setActiveTab(preLandscapeState.activeTab);
      setCustomDateRange(preLandscapeState.customDateRange);
    }
    setPreLandscapeState(null);
    if (Capacitor.isNativePlatform()) {
      try {
        await ScreenOrientation.lock({ orientation: "portrait" });
      } catch (error) {
        console.warn("Failed to return to portrait:", error);
      }
    }
  };

  // Updated function to return local dates + UTC offset
  const syncWithDateRange = (
    tabType: TabType,
    customDateRange?: { start: string; end: string }
  ) => {
    // console.log("Syncing with date range for tab:", tabType);
    let startDate: Date;
    let endDate: Date;
    let dateRangeType: string;

    if (tabType === "week") {
      dateRangeType = "week";
      const today = new Date();
      endDate = new Date(today);
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 6);
    } else if (tabType === "month") {
      dateRangeType = "month";
      const today = new Date();
      endDate = new Date(today);
      startDate = new Date(today);
      startDate.setDate(today.getDate() - 29);
    } else {
      dateRangeType = "range";
      console.log("Custom date range:", customDateRange);
      if (customDateRange) {
        console.log("Using custom date range:", customDateRange);
        startDate = new Date(customDateRange.start);
        endDate = new Date(customDateRange.end);
      } else {
        console.log("No custom date range selected");
        return;
      }
    }

    // Set time to current time for both dates (keeping your existing logic)
    const now = new Date();
    startDate.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );
    endDate.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
      now.getMilliseconds()
    );

    // Get UTC offset in +HH:MM format
    const utcOffsetMinutes = startDate.getTimezoneOffset();
    const offsetHours = Math.floor(Math.abs(utcOffsetMinutes) / 60);
    const offsetMinutes = Math.abs(utcOffsetMinutes) % 60;
    const offsetSign = utcOffsetMinutes <= 0 ? "+" : "-"; // getTimezoneOffset() returns opposite sign
    const utcOffsetString = `${offsetSign}${String(offsetHours).padStart(
      2,
      "0"
    )}:${String(offsetMinutes).padStart(2, "0")}`;

    // Format dates as YYYY-MM-DD in local timezone
    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    const startLocalDate = formatLocalDate(startDate);
    const endLocalDate = formatLocalDate(endDate);

    console.log("Sync Date Range - Tab Switch:", {
      type: dateRangeType,
      startDate: startLocalDate,
      endDate: endLocalDate,
      utcOffset: utcOffsetString,
      startTimestamp: startDate.getTime(),
      endTimestamp: endDate.getTime(),
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      localStartDateTime: startDate.toLocaleString(),
      localEndDateTime: endDate.toLocaleString(),
    });

    // Call onSync with the local date range and UTC offset
    onSync({
      start: startLocalDate,
      end: endLocalDate,
      tz: utcOffsetString,
    });
  };

  // Add this helper function at the top of your component, before the AdherenceSummaryCard component

  const formatDateRangeForTab = (
    startDate: string,
    endDate: string
  ): string => {
    const parseDate = (dateStr: string) => {
      // Handle both "9 Jun" and "2024-06-09" formats
      if (dateStr.includes("-") && dateStr.length > 7) {
        return new Date(dateStr);
      } else {
        // Assume current year if not specified
        const currentYear = new Date().getFullYear();
        const [day, month] = dateStr.split(" ");
        const monthNames = [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ];
        const monthIndex = monthNames.indexOf(month);
        return new Date(currentYear, monthIndex, parseInt(day));
      }
    };

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    const formatDate = (date: Date, includeYear = true) => {
      const day = date.getDate();
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const year = date.getFullYear().toString().slice(-2);
      return includeYear ? `${day} ${month} '${year}` : `${day} ${month}`;
    };

    const formatDateCompact = (date: Date) => {
      const month = date.toLocaleDateString("en-US", { month: "short" });
      const year = date.getFullYear().toString().slice(-2);
      return `${month} '${year}`;
    };

    const isSameMonth =
      start.getMonth() === end.getMonth() &&
      start.getFullYear() === end.getFullYear();
    const isSameYear = start.getFullYear() === end.getFullYear();

    // Calculate available width (approximate)
    const getTextWidth = (text: string): number => {
      // Approximate width calculation (adjust based on your font)
      return text.length * 7; // Rough estimate for 12px font
    };

    const maxWidth = 120; // Maximum width for the tab content

    // Try different formatting approaches
    let formattedRange = "";

    if (isSameMonth) {
      // Same month: "9–15 Jun '24"
      const startDay = start.getDate();
      const endDay = end.getDate();
      const month = start.toLocaleDateString("en-US", { month: "short" });
      const year = start.getFullYear().toString().slice(-2);
      formattedRange = `${startDay}–${endDay} ${month} '${year}`;
    } else if (isSameYear) {
      // Different months, same year: try "9 Jun – 15 Jul '24"
      const startStr = formatDate(start, false);
      const endStr = formatDate(end, false);
      const year = start.getFullYear().toString().slice(-2);
      formattedRange = `${startStr} – ${endStr} '${year}`;

      // If too long, try compact version: "Jun–Jul '24"
      if (getTextWidth(formattedRange) > maxWidth) {
        const startMonth = start.toLocaleDateString("en-US", {
          month: "short",
        });
        const endMonth = end.toLocaleDateString("en-US", { month: "short" });
        formattedRange = `${startMonth}–${endMonth} '${year}`;
      }
    } else {
      // Different years: "9 Jun '24 – 15 Jul '25"
      formattedRange = `${formatDate(start)} – ${formatDate(end)}`;

      // If too long, try compact: "Jun '24 – Jul '25"
      if (getTextWidth(formattedRange) > maxWidth) {
        formattedRange = `${formatDateCompact(start)} – ${formatDateCompact(
          end
        )}`;
      }
    }

    // Final fallbacks if still too long
    if (getTextWidth(formattedRange) > maxWidth) {
      // Try start date only: "From 9 Jun '24"
      const startStr = formatDate(start);
      const fromText = `From ${startStr}`;
      if (getTextWidth(fromText) <= maxWidth) {
        formattedRange = fromText;
      } else {
        // Last resort
        formattedRange = "Date Range";
      }
    }

    return formattedRange;
  };

  // Function to convert dates to UTC timestamps and log (for poke device)
  const handlePokeDevice = () => {
    syncWithDateRange(activeTab, {
      start: customDateRange?.start || "",
      end: customDateRange?.end || "",
    });
  };

  // Handle tab change with sync
  const handleTabChange = (tab: TabType) => {
    if (tab === "range") {
      setShowDatePicker(true);
    } else {
      setActiveTab(tab);
      // Call onSync when switching to week or month tab
      syncWithDateRange(tab);
    }
  };

  const currentDates = data?.sessions.map((session) => session.date) || [];

  const handleDateRangeSelect = (
    startDisplay: string,
    endDisplay: string,
    startISO: string,
    endISO: string
  ) => {
    console.log("Selected Date Range:", {
      startDisplay,
      endDisplay,
      startISO,
      endISO,
    });
    setCustomDateRange({ start: startDisplay, end: endDisplay });
    setActiveTab("range");
    syncWithDateRange("range", { start: startDisplay, end: endDisplay });
  };

  const formatSecondsToMMSS = (totalSeconds: number): string => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  // Calculate cycle counts per day
  const getDayCycleCount = (date: string) => {
    if (!data) return 0;
    return data.sessions
      .filter((session) => session.date === date)
      .reduce((total, session) => total + session.cycleCount, 0);
  };

  // Check if a day has any therapy sessions
  const dayHasTherapy = (date: string) => {
    if (!data) return false;
    return data.sessions.some(
      (session) =>
        session.date === date &&
        (session.timeSlots["12AM"] > 0 ||
          session.timeSlots["6AM"] > 0 ||
          session.timeSlots["12PM"] > 0 ||
          session.timeSlots["6PM"] > 0)
    );
  };

  // Get therapy sessions for a specific date and time slot
  const getTherapyDots = (
    date: string,
    timeSlot: "12AM" | "6AM" | "12PM" | "6PM"
  ) => {
    if (!data) return 0;
    const sessions = data.sessions.find((session) => session.date === date);
    return sessions?.timeSlots?.[timeSlot] || 0;
  };

  // Update the renderTabs function (replace the existing one)
  const renderTabs = () => (
    <div className="px-4 py-3" style={{ backgroundColor: "#1C1C1E" }}>
      <div className="flex justify-center">
        <div
          className="flex p-1"
          style={{
            backgroundColor: "#262626",
            borderRadius: "30px",
            width: "343px",
            height: "32px",
            fontFamily:
              "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
          }}
        >
          {["week", "month", "range"].map((tab) => {
            let tabText = "";
            if (tab === "week") {
              tabText = "Week";
            } else if (tab === "month") {
              tabText = "Month";
            } else {
              // Enhanced date range formatting
              tabText = customDateRange
                ? formatDateRangeForTab(
                    customDateRange.start,
                    customDateRange.end
                  )
                : "Date range";
            }

            return (
              <button
                key={tab}
                onClick={() => handleTabChange(tab as TabType)}
                className="flex-1 flex items-center justify-center transition-all duration-200"
                style={{
                  height: "24px",
                  borderRadius: "30px",
                  backgroundColor:
                    activeTab === tab ? "#1C1C1E" : "transparent",
                  border:
                    activeTab === tab
                      ? "0.5px solid rgba(0, 0, 0, 0.04)"
                      : "none",
                  color: activeTab === tab ? "#FFFFFF" : "#B0B4B7",
                  fontSize: "12px",
                  fontWeight: activeTab === tab ? "600" : "400",
                  lineHeight: "20px",
                  fontFamily:
                    "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
                  // Ensure text doesn't overflow
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  padding: "0 8px", // Add some padding to prevent cramped text
                }}
                title={
                  tab === "range" && customDateRange
                    ? `${customDateRange.start} – ${customDateRange.end}`
                    : tabText
                } // Show full range on hover
              >
                {tabText}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );

  // **UPDATED: Enhanced therapy grid with intelligent layout system**
  const renderTherapyGrid = (isLandscapeView = false) => {
    const timeSlots: ("12AM" | "6AM" | "12PM" | "6PM")[] = [
      "12AM",
      "6AM",
      "12PM",
      "6PM",
    ];
    const timeLabels = [
      "12:00 AM",
      "6:00 AM",
      "12:00 PM",
      "6:00 PM",
      "11:59 PM",
    ];

    // **NEW: Intelligent layout calculation to eliminate empty space**
    const getLayoutConfig = () => {
      const leftColumnWidth = isLandscapeView ? 100 : 80;
      const containerPadding = isLandscapeView ? 32 : 16;
      const availableWidth =
        window.innerWidth - leftColumnWidth - containerPadding;

      // Define minimum cell widths optimized for all resolutions
      const minCellWidth = isLandscapeView ? 75 : 48;
      const maxCellWidth = isLandscapeView ? 140 : 85;

      // Calculate ideal cell width if we distribute available space evenly
      const idealCellWidth = availableWidth / currentDates.length;

      // Determine layout mode: flex (fit-to-container) or scroll
      const shouldUseFlex = idealCellWidth >= minCellWidth;

      let cellWidth;
      if (shouldUseFlex) {
        // Flex mode: distribute space evenly, capped at maxCellWidth
        cellWidth = Math.min(maxCellWidth, idealCellWidth);
      } else {
        // Scroll mode: use minimum width to ensure readability
        cellWidth = minCellWidth;
      }

      return {
        cellWidth,
        leftColumnWidth,
        shouldUseFlex,
        totalContentWidth: cellWidth * currentDates.length,
      };
    };

    const { cellWidth, leftColumnWidth, shouldUseFlex, totalContentWidth } =
      getLayoutConfig();
    const gridRowHeight = isLandscapeView ? 40 : 32;

    return (
      <div className="mb-3">
        {/* Single Grid Container */}
        <div
          className="grid grid-cols-[80px_1fr] gap-0"
          style={{ gridTemplateColumns: `${leftColumnWidth}px 1fr` }}
        >
          {/* Fixed Left Column */}
          <div className="flex flex-col">
            {/* Cycles Count Label */}
            <div
              className="flex items-center justify-center text-orange-400 text-xs font-medium text-center leading-tight mb-2"
              style={{
                height: `${gridRowHeight}px`,
                fontSize: isLandscapeView ? "14px" : "12px",
              }}
            >
              {isClearDevice ? (
                <>
                  Duration
                  <br />
                  (mm:ss)
                </>
              ) : (
                <>
                  Cycles
                  <br />
                  count
                </>
              )}
            </div>

            {/* Time Labels */}
            <div
              className="relative"
              style={{
                height: `${(timeLabels.length - 1) * gridRowHeight + 16}px`,
              }}
            >
              {timeLabels.map((timeLabel, index) => (
                <div
                  key={timeLabel}
                  className="absolute flex items-center justify-end pr-3 text-gray-400 text-xs"
                  style={{
                    top: `${index * gridRowHeight}px`,
                    height: "16px",
                    width: `${leftColumnWidth - 4}px`,
                    fontSize: isLandscapeView ? "12px" : "10px",
                  }}
                >
                  {timeLabel}
                </div>
              ))}
            </div>

            {/* Empty space for date labels */}
            <div style={{ height: `${gridRowHeight}px`, marginTop: "8px" }} />
          </div>

          {/* Dynamic Right Column - Scrollable only when needed */}
          <div
            className={`relative ${
              shouldUseFlex ? "" : "overflow-x-scroll scrollbar-hide"
            }`}
            style={{
              scrollbarWidth: shouldUseFlex ? "auto" : "none",
              msOverflowStyle: shouldUseFlex ? "auto" : "none",
              WebkitOverflowScrolling: shouldUseFlex ? "auto" : "touch",
            }}
          >
            <div
              className="flex flex-col"
              style={{
                width: shouldUseFlex ? "100%" : `${totalContentWidth}px`,
                minWidth: shouldUseFlex ? "100%" : `${totalContentWidth}px`,
              }}
            >
              {/* Cycle Counts Row */}
              <div
                className="flex mb-2"
                style={{
                  height: `${gridRowHeight}px`,
                  justifyContent: shouldUseFlex ? "space-evenly" : "flex-start",
                }}
              >
                {currentDates.map((date) => (
                  <div
                    key={`cycles-${date}`}
                    className="flex items-center justify-center"
                    style={{
                      width: `${cellWidth}px`,
                      height: `${gridRowHeight}px`,
                      flexShrink: shouldUseFlex ? 0 : undefined,
                    }}
                  >
                    <span
                      className="text-orange-400 font-bold text-base"
                      style={{
                        fontSize: isClearDevice
                          ? isLandscapeView ? "14px" : "12px"
                          : isLandscapeView ? "18px" : "16px",
                        letterSpacing: isClearDevice ? "-0.3px" : undefined,
                      }}
                    >
                      {isClearDevice
                        ? formatSecondsToMMSS(getDayCycleCount(date))
                        : getDayCycleCount(date)}
                    </span>
                  </div>
                ))}
              </div>

              {/* Therapy Grid */}
              <div
                className="border border-gray-600 rounded overflow-hidden"
                style={{
                  display: "grid",
                  gridTemplateColumns: shouldUseFlex
                    ? `repeat(${currentDates.length}, 1fr)`
                    : `repeat(${currentDates.length}, ${cellWidth}px)`,
                  gridTemplateRows: `repeat(${timeSlots.length}, ${gridRowHeight}px)`,
                  width: shouldUseFlex ? "100%" : "auto",
                }}
              >
                {timeSlots.map((timeSlot, timeIndex) =>
                  currentDates.map((date, dateIndex) => {
                    const therapyDots = getTherapyDots(date, timeSlot);
                    const dayEmpty = !dayHasTherapy(date);
                    const isLastRow = timeIndex === timeSlots.length - 1;
                    const isLastCol = dateIndex === currentDates.length - 1;

                    return (
                      <div
                        key={`${date}-${timeSlot}`}
                        className="flex items-center justify-center relative"
                        style={{
                          backgroundColor: "#1C1C1E",
                          borderRight: !isLastCol
                            ? "1px solid #363636"
                            : "none",
                          borderBottom: !isLastRow
                            ? "1px solid #363636"
                            : "none",
                          backgroundImage: dayEmpty
                            ? "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(38, 38, 38, 1) 2px, rgba(38, 38, 38, 1) 4px)"
                            : "none",
                        }}
                      >
                        {therapyDots > 0 && (
                          <div className="flex items-center">
                            <div
                              className="grid grid-cols-4 gap-1"
                              style={{
                                width: isLandscapeView ? "40px" : "30px",
                                height: isLandscapeView ? "18px" : "14px",
                              }}
                            >
                              {Array.from(
                                { length: Math.min(therapyDots, 8) },
                                (_, i) => (
                                  <div
                                    key={i}
                                    style={{
                                      width: isLandscapeView ? "5px" : "4px",
                                      height: isLandscapeView ? "5px" : "4px",
                                      backgroundColor: "#00FFFF",
                                      border: "0.5px solid #00FFFF",
                                      borderRadius: "50%",
                                    }}
                                  />
                                )
                              )}
                            </div>
                            {therapyDots > 8 && (
                              <span
                                className="text-white text-xs font-bold ml-1"
                                style={{
                                  color: "#00FFFF",
                                  fontSize: isLandscapeView ? "14px" : "12px",
                                }}
                              >
                                +
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {/* Date Labels Row */}
              <div
                className="flex mt-2"
                style={{
                  height: `${gridRowHeight}px`,
                  justifyContent: shouldUseFlex ? "space-evenly" : "flex-start",
                }}
              >
                {currentDates.map((date) => {
                  const d = new Date(date);
                  const dayMonth = d.toLocaleDateString("en-GB", {
                    day: "2-digit",
                    month: "short",
                  }); // e.g., "18 Jun"
                  const year = d.getFullYear().toString().slice(-2); // e.g., "24"

                  return (
                    <div
                      key={`date-${date}`}
                      className="flex flex-col items-center justify-center text-gray-400 text-xs leading-tight"
                      style={{
                        width: `${cellWidth}px`,
                        height: `${gridRowHeight}px`,
                        fontSize: isLandscapeView ? "12px" : "10px",
                        flexShrink: shouldUseFlex ? 0 : undefined,
                      }}
                    >
                      <div>{dayMonth}</div>
                      <div>{year}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // **NEW: Enhanced stats rendering for landscape**
  const renderStats = (isLandscapeView = false) => {
    if (!data) return null;

    return (
      <div
        className={`grid grid-cols-3 gap-4 ${
          isLandscapeView ? "pt-6 " : "pt-4 pb-4"
        } border-t border-gray-600`}
        style={{
          padding: isLandscapeView ? "24px 0 0 0" : "16px 0 0 0",
          paddingBottom: isLandscapeView ? "0" : "16px",
        }}
      >
        <div className="text-center">
          <div
            className="text-2xl font-bold text-green-400"
            style={{ fontSize: isLandscapeView ? "28px" : "24px" }}
          >
            {data.stats.therapyDays}
          </div>
          <div
            className="text-sm text-gray-400"
            style={{ fontSize: isLandscapeView ? "14px" : "12px" }}
          >
            Therapy Days
          </div>
        </div>
        <div className="text-center">
          <div
            className="text-2xl font-bold text-cyan-400"
            style={{ fontSize: isLandscapeView ? "28px" : "24px" }}
          >
            {data.stats.missedDays}
          </div>
          <div
            className="text-sm text-gray-400"
            style={{ fontSize: isLandscapeView ? "14px" : "12px" }}
          >
            Missed days
          </div>
        </div>
        <div className="text-center">
          <div
            className="text-2xl font-bold text-orange-400"
            style={{ fontSize: isLandscapeView ? "28px" : "24px" }}
          >
            {data.stats.avgAdherence}%
          </div>
          <div
            className="text-sm text-gray-400"
            style={{ fontSize: isLandscapeView ? "14px" : "12px" }}
          >
            Avg Adherence
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div>
          <AdherenceLoading />
        </div>
      );
    }

    if (!data) {
      return (
        <div className="flex flex-col items-center justify-center py-16">
          <button
            onClick={handlePokeDevice}
            className="bg-gradient-to-r from-cyan-500 to-sky-500 text-white px-8 py-3 rounded-full font-medium flex items-center gap-2"
            style={{
              background: "linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)",
              borderRadius: "30px",
              fontFamily:
                "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
              fontWeight: "600",
              fontSize: "16px",
              padding: "12px 25px",
              height: "46px",
            }}
          >
            <IonIcon
              icon={sendSharp}
              style={{
                fontSize: "18px",
                color: "white",
              }}
            />
            Fetch from Device
          </button>
        </div>
      );
    }

    return (
      <div>
        {renderTherapyGrid(false)}
        {renderStats(false)}
      </div>
    );
  };

  return (
    <>
      <div
        className="rounded-xl mb-4 overflow-hidden"
        style={{ backgroundColor: "#1C1C1E" }}
      >
        {/* Main Header */}
        <div
          className="flex justify-between items-center px-4 py-3 border-b"
          style={{ borderColor: "rgba(255, 255, 255, 0.1)" }}
        >
          <h3 className="text-white text-lg font-medium">ADHERENCE SUMMARY</h3>
          {/* **NEW: Conditional expand button - only show when data exists** */}
          {data && (
            <button
              onClick={forceEnterLandscape}
              className="flex items-center justify-center"
              style={{
                minWidth: "30px",
                minHeight: "30px",
              }}
            >
              <svg
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  flex: "none",
                }}
              >
                <rect width="30" height="30" rx="15" fill="#363636" />
                <path
                  d="M20.8346 9.1665L15.8346 9.1665L15.8346 10.8332L19.168 10.8332L19.168 14.1665L20.8346 14.1665L20.8346 9.1665ZM14.168 19.1665L10.8346 19.1665L10.8346 15.8332L9.16797 15.8332L9.16797 20.8332L14.168 20.8332L14.168 19.1665Z"
                  fill="#B0B4B7"
                />
              </svg>
            </button>
          )}
        </div>

        {/* Device Disconnection Notice */}
        {!isConnected && storedAdherenceDeviceId && data && (
          <div
            className="px-4 py-2 border-b"
            style={{
              backgroundColor: "#2A2A2C",
              borderColor: "rgba(255, 255, 255, 0.1)",
            }}
          >
            <p
              className="text-sm font-normal"
              style={{ color: "rgba(255, 255, 255, 0.6)" }}
            >
              Showing data from device: {storedAdherenceDeviceId}
            </p>
          </div>
        )}

        {/* Tabs Section */}
        {isConnected && renderTabs()}

        {/* Content */}
        <div className="p-4 pb-0">{renderContent()}</div>
      </div>

      {/* **NEW: Full Screen Landscape Modal** */}
      {isLandscape && forcedLandscape && (
        <IonModal
          isOpen={isLandscape && forcedLandscape}
          showBackdrop={false}
          className="landscape-modal"
          enterAnimation={undefined}
          leaveAnimation={undefined}
        >
          <div className="min-h-screen bg-black text-white">
            <div
              style={{
                padding: "16px",
                background: "#000000",
                height: "100vh",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Header Row with Back Button */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: "16px",
                  minHeight: "40px",
                }}
              >
                <button
                  onClick={exitLandscape}
                  style={{
                    background: "transparent",
                    border: "none",
                    cursor: "pointer",
                    padding: "0",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    marginRight: "16px",
                    transition: "opacity 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.opacity = "0.8";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.opacity = "1";
                  }}
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 18L9 12L15 6"
                      stroke="#B0B4B7"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                <h3
                  className="text-white text-lg font-medium"
                  style={{
                    fontFamily:
                      "Figtree, -apple-system, BlinkMacSystemFont, sans-serif",
                    fontWeight: "600",
                    fontSize: "20px",
                    margin: 0,
                  }}
                >
                  ADHERENCE SUMMARY
                </h3>
              </div>

              {/* Content Area */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                  maxHeight: "calc(100vh - 72px)", // Account for header height
                }}
              >
                {data ? (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "100%",
                      padding: "0 16px 16px 16px",
                    }}
                  >
                    {/* Therapy Grid - Takes available space */}
                    <div style={{ flexGrow: 1, marginBottom: "16px" }}>
                      {renderTherapyGrid(true)}
                    </div>

                    {/* Stats - Always visible at bottom */}
                    <div style={{ flexShrink: 0 }}>{renderStats(true)}</div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center flex-1">
                    <p className="text-gray-400 text-center mb-4">
                      No data available
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </IonModal>
      )}

      {/* **ENHANCED: Responsive IonDateRangeModal with proper height handling** */}
      <IonDateRangeModal
        isOpen={showDatePicker}
        onClose={() => setShowDatePicker(false)}
        onDateRangeSelect={handleDateRangeSelect}
        title="Date range"
        buttonText="Done"
        mode="range"
        primaryGradient="linear-gradient(90deg, #01B7C4 0%, #00ACD7 100%)"
        backgroundColor="#222222"
        inputBackgroundColor="#262626"
        modalWidth={(() => {
          const screenWidth = window.innerWidth;
          const screenHeight = window.innerHeight;

          // Handle very small screens (iPhone SE, etc.)
          if (screenWidth <= 375 || screenHeight <= 667) {
            return `${Math.min(screenWidth * 0.9, 320)}px`;
          }
          // Handle small mobile screens
          else if (screenWidth <= 480) {
            return `${Math.min(screenWidth * 0.85, 340)}px`;
          }
          // Default for larger screens
          else {
            return "362px";
          }
        })()}
        modalHeight={(() => {
          const screenHeight = window.innerHeight;
          // Handle different screen heights
          if (screenHeight <= 667) {
            // iPhone SE and similar
            return "80vh";
          } else if (screenHeight <= 736) {
            // iPhone 6/7/8 Plus
            return "85vh";
          } else {
            return "auto";
          }
        })()}
        maxDate={new Date().toISOString()}
      />

      <style>{`
      .scrollbar-hide {
        -ms-overflow-style: none;
        scrollbar-width: none;
      }
      .scrollbar-hide::-webkit-scrollbar {
        display: none;
      }
      
      /* Landscape modal styling */
      .landscape-modal {
        --width: 100%;
        --height: 100%;
        --max-width: none;
        --max-height: none;
      }

      /* **FIXED: Comprehensive date picker height and width responsiveness** */
      ion-modal.date-range-modal,
      .ion-modal.date-range-modal {
        --height: auto;
        --max-height: 85vh;
        --overflow: hidden;
      }
      
      ion-modal.date-range-modal .modal-wrapper,
      .ion-modal.date-range-modal .modal-wrapper {
        max-height: 85vh;
        overflow-y: auto;
      }
      
      /* iPhone SE and very small screens */
      @media (max-height: 667px) {
        ion-modal.date-range-modal,
        .ion-modal.date-range-modal {
          --height: 80vh;
          --max-height: 80vh;
        }
        
        ion-modal.date-range-modal .modal-wrapper,
        .ion-modal.date-range-modal .modal-wrapper {
          height: 80vh;
          max-height: 80vh;
        }
      }
      
      /* Very short screens */
      @media (max-height: 600px) {
        ion-modal.date-range-modal,
        .ion-modal.date-range-modal {
          --height: 75vh;
          --max-height: 75vh;
        }
      }
      
      /* Width constraints for small screens */
      @media (max-width: 375px) {
        ion-modal.date-range-modal,
        .ion-modal.date-range-modal {
          --width: 90vw;
          --max-width: 320px;
        }
      }
      
      @media (max-width: 480px) {
        ion-modal.date-range-modal,
        .ion-modal.date-range-modal {
          --width: 85vw;
          --max-width: 340px;
        }
      }
      
      /* Landscape orientation adjustments */
      @media (orientation: landscape) and (max-height: 500px) {
        ion-modal.date-range-modal,
        .ion-modal.date-range-modal {
          --height: 90vh;
          --max-height: 90vh;
          --width: 70vw;
          --max-width: 400px;
        }
      }
    `}</style>
    </>
  );
};

// Mock data for testing (updated with recent dates and some empty days)
export const mockAdherenceData: AdherenceData = {
  stats: {
    therapyDays: 20,
    missedDays: 10,
    avgAdherence: 60,
  },
  sessions: [
    {
      date: "9 Jun",
      timeSlots: {
        "12AM": 0,
        "6AM": 5,
        "12PM": 8,
        "6PM": 10,
      },
      cycleCount: 40,
    },
    {
      date: "8 Jun",
      timeSlots: {
        "12AM": 0,
        "6AM": 0,
        "12PM": 5,
        "6PM": 5,
      },
      cycleCount: 10,
    },
    {
      date: "6 Jun",
      timeSlots: {
        "12AM": 0,
        "6AM": 5,
        "12PM": 0,
        "6PM": 5,
      },
      cycleCount: 45,
    },
    {
      date: "5 Jun",
      timeSlots: {
        "12AM": 5,
        "6AM": 5,
        "12PM": 5,
        "6PM": 5,
      },
      cycleCount: 20,
    },
    {
      date: "3 Jun",
      timeSlots: {
        "12AM": 5,
        "6AM": 0,
        "12PM": 0,
        "6PM": 5,
      },
      cycleCount: 30,
    },
  ],
};

export default AdherenceSummaryCard;
